<template>
  <div>
    <div id="actions" class="row px-2">
      <div :class="{'text-center w-100': center}">
        <button id="fileinput-button" type="button"
                class="btn btn-ss btn-ss-standout">
          <i class="la la-upload" /> {{ uploadButtonText }}
        </button>
        <button v-show="!singleUpload" ref="cancel_all_btn"
                type="button"
                class="btn btn-ss btn-ss-danger d-none"
                @click="cancelAllQueued">
          <i class="la la-times" /> Cancel All
        </button>
        <button v-show="!singleUpload" ref="retry_all_btn"
                type="button"
                class="btn btn-ss btn-ss d-none"
                @click="retryAllFailed">
          <i class="flaticon2-refresh" /> Retry Any Failed
        </button>
      </div>
    </div>
    <div ref="files_section" class="row files-section d-none">
      <hr>
      <div class="col-12">
        <div id="previews" class="table table-striped files">
          <!--
            START OF DROPZONE TOW TEMPLATE
            Dropzone works similarly to Vue here: this div with id="template" is duplicated like a v-for,
            so we can't add any vue bindings here since they will be destroyed (Dropzone doesn't know about Vue).
           -->
          <div id="template" class="m-4 border-bottom border-gray">
            <div class="my-3 d-flex flex-row align-items-center">
              <button type="button" class="btn btn-ss btn-icon btn-sm mr-2 flex-shrink-0 cancel-file d-none">
                <i class="la la-times" />
              </button>
              <button type="button" class="btn btn-ss btn-icon btn-sm mr-2 flex-shrink-0 retry-file d-none">
                <i class="flaticon2-refresh" />
              </button>
              <div>
                <span class="name" data-dz-name /><span>: </span>
              </div>
              <span class="size flex-shrink-0 mx-2" data-dz-size />
              <strong class="error text-ss-danger data-dz-errormessage" data-dz-errormessage />
              <span class="d-none upload-complete badge badge-success ml-auto">
                <i class="la la-check-circle mr-2" />Upload Complete. </span>
              <span data-dz-building-chunks class="building-chunks">
                <span class="badge badge-ss">Rebuilding Chunks...</span>
              </span>
            </div>
            <div>
              <div class="progress progress-striped" role="progressbar"
                   aria-valuemin="0" aria-valuemax="100"
                   aria-valuenow="0">
                <div class="progress-bar" style="width:0%;" />
              </div>
            </div>
          </div>
          <!-- END OF DROPZONE ROW TEMPLATE -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * @alias {import('@/vendor/sharpstream/metronic-theme-demo1/src/Resources/theme/default/demo1/dist/assets/js/pages/crud/file-upload/dropzonejs.js').default} DZ
 */

/**
 * @typedef { Object } DropzoneStatic
 * @property { 'added' } ADDED
 * @property { 'queued' } QUEUED
 * @property { 'queued' } ACCEPTED
 * @property { 'uploading' } UPLOADING
 * @property { 'uploading' } PROCESSING
 * @property { 'canceled' } CANCELED
 * @property { 'error' } ERROR
 * @property { 'success' } SUCCESS
 */

/**
 * Dropzone from `window` (provided by metronic demo1)
 *
 * **Docs**: https://docs.dropzone.dev/
 *
 * **Source** (since docs are bad): https://github.com/dropzone/dropzone/blob/main/src/dropzone.js
 *
 * @type DropzoneStatic
 */
const Dropzone = window.Dropzone;

export default {
  props: {
    postUrl: {
      type: String,
      required: true,
    },
    mimeTypes: {
      type: String,
      required: false,
      default: '.mp3',
    },
    singleUpload: {
      type: Boolean,
      required: false,
      default: false,
    },
    center: {
      type: Boolean,
      required: false,
      default: false,
    },
    uploadButtonText: {
      type: String,
      required: false,
      default: 'Upload Files',
    },
  },
  data: () => ({
    dropzone: null,
  }),
  mounted() {
    const preview_node = document.querySelector("#template");
    preview_node.id = "";
    const preview_template = preview_node.parentNode.innerHTML;
    preview_node.parentNode.removeChild(preview_node);

    const file_list = new Array;

    const dropzone = new Dropzone(document.getElementById(`actions`), {
      url: this.postUrl,
      headers: {
        "X-CSRF-TOKEN": document.head.querySelector('meta[name="csrf-token"]').content,
      },
      paramName: "file",
      previewTemplate: preview_template,
      dictFileTooBig: 'File is too big',
      previewsContainer: "#previews",
      clickable: "#fileinput-button",
      method: "POST",
      maxFilesize: 400000000,
      chunkSize: 10000000,
      chunking: true,
      maxFiles: this.singleUpload ? 1 : null,
      parallelUploads: this.singleUpload ? 1 : 2,
      parallelChunkUploads: false,
      timeout: -1,
      acceptedFiles: this.mimeTypes,
      // If true, the individual chunks of a file are being uploaded simultaneously.
      // parellelChunks:true,
      chunksUploaded: function (file, done) {
        const file_element = file.previewElement;
        $(file_element).find('.progress-bar').width('100%');
        done();
      },
      // autoProcessQueue: false,
    });

    dropzone.on("addedfile", (file) => {
      this.$refs.files_section.classList.remove('d-none');
      this.$refs.cancel_all_btn?.classList.remove('d-none');

      if (dropzone.getAcceptedFiles().filter(f => [
        Dropzone.CANCELED,
        Dropzone.ERROR,
      ].includes(f.status)).length) {
        this.$refs.retry_all_btn?.classList.remove('d-none');
      } else {
        this.$refs.retry_all_btn?.classList.add('d-none');
      }

      const cancel_btn = file.previewElement.querySelector('.cancel-file');
      cancel_btn.addEventListener('click', () => {
        dropzone.cancelUpload(file);
      });
      cancel_btn.classList.remove('d-none');

      const retry_btn = file.previewElement.querySelector('.retry-file');
      retry_btn.addEventListener(`click`, () => {
        this.retryFile(file);
      });
    });

    dropzone.on("canceled", (file) => {
      const cancel_btn = file.previewElement.querySelector('.cancel-file');
      const retry_btn = file.previewElement.querySelector('.retry-file');

      cancel_btn.classList.add('d-none');
      retry_btn.classList.remove('d-none');
      this.$emit("cancelled");
    });

    dropzone.on("canceledmultiple", (...args) => {
      this.$emit("cancelled-multiple", ...args);
    });

    dropzone.on("success", async (file) => {
      file.previewElement.querySelector('.upload-complete').classList.remove('d-none');
      file.previewElement.querySelector('.cancel-file').classList.add('d-none');

      if (this.singleUpload) {
        const xhr_response = file.xhr.response;

        // asset is given in xhr, otherwise we will try to fetch it a couple of times before giving up
        const max_attempts = 3; // try this many times
        const filter_str = `filters=original_name:eq:${file.name}`;
        const resolveAsset = async (attempt = 0) => {
          const asset =
            JSON.parse(xhr_response).data?.storage_asset ?? // asset as parsed from json str in xhr OR
            (await axios.get(`/api/v1/storage?${filter_str}&sort=created_at|desc&per_page=1`)).data.data[0]; // result from fetching by name from API

          if (!asset && attempt <= max_attempts) { // if no asset after awaiting API, try again (up to `max_attempts`)
            return resolveAsset(attempt + 1);
          }
          return asset; // if we have an asset (or we ran out of attempts and have undefined...), return it
        };

        const storage_asset = await resolveAsset();

        EventBus.$emit("storage_asset_uploaded", storage_asset);
      }
    });

    dropzone.on("error", (file, error) => {
      this.$refs.retry_all_btn?.classList.remove('d-none');
      const error_el = file.previewElement.querySelector(".data-dz-errormessage");
      const error_message = error.e && error.e.message ? error.e.message : "";
      error_el.innerHtml = `Something went wrong, please try to upload again : ${error_message}`;
      this.$emit("error");
    });

    dropzone.on("queuecomplete", () => {
      EventBus.$emit("upload-success");
      this.$refs.cancel_all_btn?.classList.add('d-none');

      this.$emit(`success`);
    });

    // // hook for cancellation of in-progress upload
    // document.querySelector("#actions .cancel").onclick = function () {
    //   my_dropzone.removeAllFiles(true);
    // };

    dropzone.on("success", (file) => {
      const name_el = file.previewElement.querySelector("[data-dz-name]");
      name_el.innerHTML = file.name;

      file_list.push({
        "originalFileName": file.name,
        "fileName": file.name,
        "fileId": file_list.length,
      });
    });

    dropzone.on("uploadprogress", (file, progress) => {
      this.$emit("uploading-progress", progress);

      if (progress === 100) {
        return;
      } else {
        const preview_el = $(file.previewElement);
        if (progress >= 80) {
          preview_el.find('.building-chunks').show();
        }
        preview_el.find('.progress-bar').width(progress + '%');
      }
    });

    this.dropzone = dropzone;
  },
  methods: {
    cancelAllQueued() {
      if (this.dropzone) {
        this.dropzone.getAcceptedFiles().forEach((file) => {
          this.dropzone.cancelUpload(file);
        });
      }
    },
    retryAllFailed() {
      if (this.dropzone) {
        this.dropzone.getAcceptedFiles().filter(file => [
          Dropzone.ERROR,
          Dropzone.CANCELED,
        ].includes(file.status)).forEach((file) => {
          this.retryFile(file);
        });
      }
    },
    retryFile(file) {
      const retry = new File([file], file.name, { type: file.type });
      retry.status = Dropzone.ADDED;
      retry.accepted = true;
      this.dropzone.removeFile(file);
      this.dropzone.addFile(retry);

      const cancel_btn = file.previewElement.querySelector('.cancel-file');
      const retry_btn = file.previewElement.querySelector('.retry-file');
      cancel_btn.classList.remove('d-none');
      retry_btn.classList.add('d-none');
    },
  },
};
</script>

<style lang="scss">
@use "sass:map";

@use "~AssetsSass/_variables.scss" as *;
@use "~AssetsSass/badges.scss";

.progress-bar {
  background-color: map.get($ss, 'primary');
}

.building-chunks {
  display: none;
}

.dz-complete {
  .building-chunks {
    display: none !important;
  }

  .progress {
    display: none !important;
  }
}

.file-row {
  margin: 20px;
  border-bottom: 1px solid #ccc;
}

.total-progress-section .complete {
  display: none;
  color: green;
}
</style>
